'use client';

import { useControls } from 'leva';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CameraFrame } from './scene/SkyCamera';
import { SKY_RIG, type CameraRig } from './cameraRig';
import { useCameraControls } from './useCameraControls';
import {
  CLOUD_LIGHTING_DEFAULTS,
  CLOUD_INTERACTION_DEFAULTS,
  INTRO_DEFAULTS,
  POST_DEFAULTS,
  TRAFFIC_DEFAULTS,
  STARS_DEFAULTS,
  updateSky,
  type IntroState,
  type SkyState,
} from './skyStore';

export interface SkyControlDefaults {
  cloudsEnabled?: boolean;
  postEnabled?: boolean;
  stars?: Partial<typeof STARS_DEFAULTS>;
  rig?: CameraRig;
}

const WORKER_START_DELAY = 1100;

export const useSkyWorker = (
  defaults: SkyControlDefaults = {}
): {
  worker: Worker | null;
  workerRef: MutableRefObject<Worker | null>;
  badgeRef: MutableRefObject<HTMLDivElement | null>;
  writeFps: (fps: number) => void;
  writeCamera: (camera: CameraFrame) => void;
  markReady: () => void;
  ready: boolean;
  intro: IntroState;
  cameraFrameRef: MutableRefObject<CameraFrame | null>;
  subscribeCamera: (listener: (camera: CameraFrame) => void) => () => void;
} => {
  const [worker, setWorker] = useState<Worker | null>(null);
  const [ready, setReady] = useState(false);
  // Keeping camera frames outside React state avoids rendering the site
  // layout on every frame, in both worker and fallback modes.
  const cameraFrameRef = useRef<CameraFrame | null>(null);
  const cameraListeners = useRef(new Set<(camera: CameraFrame) => void>());
  const subscribeCamera = useCallback(
    (listener: (camera: CameraFrame) => void) => {
      cameraListeners.current.add(listener);
      if (cameraFrameRef.current) listener(cameraFrameRef.current);
      return () => {
        cameraListeners.current.delete(listener);
      };
    },
    []
  );
  const writeCamera = useCallback((camera: CameraFrame) => {
    cameraFrameRef.current = camera;
    for (const listener of cameraListeners.current) listener(camera);
  }, []);
  const markReady = useCallback(() => setReady(true), []);
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  workerRef.current = worker;

  const writeFps = useCallback((fps: number) => {
    if (badgeRef.current) {
      badgeRef.current.textContent = `${Math.round(fps)} fps`;
    }
  }, []);

  useEffect(() => {
    let w: Worker | null = null;
    const start = () => {
      w = new Worker(new URL('./sky.worker.tsx', import.meta.url), {
        type: 'module',
      });
      w.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as
          | { type?: string; fps?: number; payload?: unknown }
          | undefined;
        if (data?.type === 'sky-fps' && typeof data.fps === 'number') {
          writeFps(data.fps);
        } else if (data?.type === 'sky-camera') {
          writeCamera((data as unknown as { camera: CameraFrame }).camera);
        } else if (data?.type === 'sky-ready') {
          markReady();
        } else if (data?.type === 'error') {
          console.error('[sky worker]', data.payload);
        }
      });
      w.addEventListener('error', (event) => {
        console.error(
          '[sky worker crash]',
          event.message,
          `${event.filename}:${event.lineno}:${event.colno}`
        );
      });
      setWorker(w);
    };

    // Wait out the intro, then take the first idle slot (with a hard cap so
    // a busy tab still gets its sky).
    let idle: number | undefined;
    const timer = setTimeout(() => {
      const ric = (
        window as unknown as {
          requestIdleCallback?: (
            cb: () => void,
            opts?: { timeout: number }
          ) => number;
        }
      ).requestIdleCallback;
      if (ric) idle = ric(start, { timeout: 600 });
      else start();
    }, WORKER_START_DELAY);

    return () => {
      clearTimeout(timer);
      const cic = (
        window as unknown as { cancelIdleCallback?: (id: number) => void }
      ).cancelIdleCallback;
      if (idle != null && cic) cic(idle);
      w?.terminate();
    };
  }, [writeCamera, writeFps, markReady]);

  const sky = useControls('sky', {
    live: { value: true },
    timeOfDay: { value: 12, min: 0, max: 23.99, step: 0.05 },
    cloudCoverage: { value: 0.5, min: 0, max: 1, step: 0.01 },
    clouds: { value: defaults.cloudsEnabled ?? true },
  });

  const cloudInteraction = useControls(
    'cloud interaction',
    {
      enabled: { value: CLOUD_INTERACTION_DEFAULTS.enabled },
      trailDuration: {
        value: CLOUD_INTERACTION_DEFAULTS.trailDuration,
        label: 'trail fade (s)',
        min: 0.2,
        max: 3,
        step: 0.1,
      },
      fullStrengthSpeed: {
        value: CLOUD_INTERACTION_DEFAULTS.fullStrengthSpeed,
        label: 'full effect (px/s)',
        min: 100,
        max: 2400,
        step: 50,
      },
      minRadius: {
        value: CLOUD_INTERACTION_DEFAULTS.minRadius,
        label: 'min radius (px)',
        min: 0,
        max: 400,
        step: 4,
      },
      radiusEasing: {
        value: CLOUD_INTERACTION_DEFAULTS.radiusEasing,
        label: 'radius easing (s)',
        min: 0.01,
        max: 1,
        step: 0.01,
      },
      edgeSoftness: {
        value: CLOUD_INTERACTION_DEFAULTS.edgeSoftness,
        label: 'edge softness',
        min: 0.05,
        max: 1,
        step: 0.01,
      },
      radius: {
        value: CLOUD_INTERACTION_DEFAULTS.radius,
        label: 'max radius (px)',
        min: 24,
        max: 400,
        step: 4,
      },
      strength: {
        value: CLOUD_INTERACTION_DEFAULTS.strength,
        min: 0,
        max: 1,
        step: 0.01,
      },
    },
    { collapsed: true, render: (get) => Boolean(get('sky.clouds')) }
  );

  const cloudLighting = useControls(
    'cloud lighting',
    {
      minIrradiance: {
        value: CLOUD_LIGHTING_DEFAULTS.minIrradiance,
        label: 'minimum light',
        min: 0,
        max: 5,
        step: 0.005,
        hint: 'Minimum cloud lighting and water ambient light, applied during both day and night. Zero disables the extra lighting.',
      },
      skyLightScale: {
        value: CLOUD_LIGHTING_DEFAULTS.skyLightScale,
        label: 'sky light',
        min: 0,
        max: 5,
        step: 0.01,
        hint: 'Strength of sky lighting scattered through the clouds.',
      },
      powderScale: {
        value: CLOUD_LIGHTING_DEFAULTS.powderScale,
        label: 'powder strength',
        min: 0,
        max: 1,
        step: 0.01,
        hint: 'Cloud shading contrast from the powder effect. Zero disables it.',
      },
      powderExponent: {
        value: CLOUD_LIGHTING_DEFAULTS.powderExponent,
        label: 'powder falloff',
        min: 1,
        max: 500,
        step: 1,
        hint: 'How quickly the powder effect changes with cloud density.',
      },
    },
    { collapsed: true }
  );

  const stars = useControls('stars', {
    intensity: {
      value: defaults.stars?.intensity ?? STARS_DEFAULTS.intensity,
      min: 0,
      max: 150,
      step: 1,
    },
    forceNight: {
      value: defaults.stars?.forceNight ?? STARS_DEFAULTS.forceNight,
    },
  });

  const rig = defaults.rig ?? SKY_RIG;
  const camera = useCameraControls(rig);

  const [water] = useControls(
    `water · ${rig.id}`,
    () => ({
      speed: { value: rig.waterDefaults.speed, min: 0, max: 3, step: 0.05 },
      rippleSize: {
        value: rig.waterDefaults.rippleSize,
        min: 0.1,
        max: 10,
        step: 0.1,
      },
      distortion: {
        value: rig.waterDefaults.distortion,
        label: 'ripple strength',
        min: 0,
        max: 20,
        step: 0.1,
      },
      roughness: {
        value: rig.waterDefaults.roughness,
        min: 0.05,
        max: 1,
        step: 0.01,
      },
      color: { value: rig.waterDefaults.color },
      sunlight: {
        value: rig.waterDefaults.sunlight,
        min: 0,
        max: 3,
        step: 0.05,
      },
    }),
    { render: () => rig.sceneMode === 'water' },
    [rig]
  );

  const [traffic] = useControls(
    'traffic',
    () => ({
      trailOpacity: {
        value: TRAFFIC_DEFAULTS.trailOpacity,
        min: 0,
        max: 1,
        step: 0.01,
      },
      trailSeconds: {
        value: TRAFFIC_DEFAULTS.trailSeconds,
        min: 0,
        max: 600,
        step: 10,
      },
      dotSize: {
        value: TRAFFIC_DEFAULTS.dotSize,
        min: 1,
        max: 12,
        step: 0.5,
      },
      reticleSize: {
        value: TRAFFIC_DEFAULTS.reticleSize,
        min: 8,
        max: 60,
        step: 1,
      },
      reticleOnHover: { value: TRAFFIC_DEFAULTS.reticleOnHover },
    }),
    { render: () => rig.traffic },
    [rig]
  );

  const intro = useControls('intro', {
    fadeMs: { value: INTRO_DEFAULTS.fadeMs, min: 0, max: 4000, step: 50 },
    cloudsDelay: {
      value: INTRO_DEFAULTS.cloudsDelay,
      min: 0,
      max: 4,
      step: 0.1,
    },
    cloudsDuration: {
      value: INTRO_DEFAULTS.cloudsDuration,
      min: 0.2,
      max: 8,
      step: 0.1,
    },
  });

  const post = useControls(
    'post',
    {
      enabled: { value: defaults.postEnabled ?? true },
      lensFlare: { value: POST_DEFAULTS.lensFlare, label: 'lens flare' },
      flareGhosts: { value: POST_DEFAULTS.flareGhosts, label: 'flare ghosts' },
      flareGhostSize: {
        value: POST_DEFAULTS.flareGhostSize,
        label: 'ghost size',
        min: 0.01,
        max: 1,
        step: 0.01,
      },
      flareGlareSize: {
        value: POST_DEFAULTS.flareGlareSize,
        label: 'glare size',
        min: 0.01,
        max: 0.5,
        step: 0.01,
      },
      flareStarPoints: {
        value: POST_DEFAULTS.flareStarPoints,
        label: 'star points',
        min: 2,
        max: 12,
        step: 1,
      },
      flareAnamorphic: {
        value: POST_DEFAULTS.flareAnamorphic,
        label: 'anamorphic',
      },
      lensFlareIntensity: {
        value: POST_DEFAULTS.lensFlareIntensity,
        label: 'flare intensity',
        min: 0,
        max: 1,
        step: 0.01,
      },
      pixelation: { value: POST_DEFAULTS.pixelation },
      noiseOpacity: {
        value: POST_DEFAULTS.noiseOpacity,
        min: 0,
        max: 0.2,
        step: 0.005,
      },
      bloomIntensity: {
        value: POST_DEFAULTS.bloomIntensity,
        min: 0,
        max: 20,
        step: 0.01,
      },
      bloomThreshold: {
        value: POST_DEFAULTS.bloomThreshold,
        min: 0,
        max: 1,
        step: 0.01,
      },
      bloomSmoothing: {
        value: POST_DEFAULTS.bloomSmoothing,
        min: 0,
        max: 1,
        step: 0.01,
      },
      scanlineDensity: {
        value: POST_DEFAULTS.scanlineDensity,
        min: 0,
        max: 5,
        step: 0.1,
      },
      scanlineOpacity: {
        value: POST_DEFAULTS.scanlineOpacity,
        min: 0,
        max: 1,
        step: 0.01,
      },
      saturation: {
        value: POST_DEFAULTS.saturation,
        min: -1,
        max: 1,
        step: 0.01,
      },
      brightness: {
        value: POST_DEFAULTS.brightness,
        min: -1,
        max: 1,
        step: 0.01,
      },
      contrast: { value: POST_DEFAULTS.contrast, min: -1, max: 1, step: 0.01 },
      vignetteOffset: {
        value: POST_DEFAULTS.vignetteOffset,
        min: 0,
        max: 1,
        step: 0.01,
      },
      vignetteDarkness: {
        value: POST_DEFAULTS.vignetteDarkness,
        min: 0,
        max: 2,
        step: 0.05,
      },
      fisheyeStrength: {
        value: POST_DEFAULTS.fisheyeStrength,
        min: -1,
        max: 1,
        step: 0.01,
      },
      fisheyeZoom: {
        value: POST_DEFAULTS.fisheyeZoom,
        min: 0.5,
        max: 1.5,
        step: 0.01,
      },
    },
    { collapsed: true }
  );

  useEffect(() => {
    const { enabled: postEnabled, ...postValues } = post;
    const state: Partial<SkyState> = {
      hour: sky.live ? null : sky.timeOfDay,
      coverage: sky.cloudCoverage,
      cloudsEnabled: sky.clouds,
      cloudLighting,
      cloudInteraction,
      postEnabled,
      post: postValues,
      camera,
      sceneMode: rig.sceneMode,
      water,
      intro,
      traffic: { ...TRAFFIC_DEFAULTS, ...traffic },
      stars,
    };
    updateSky(state);
    worker?.postMessage({ type: 'sky-state', state });
  }, [
    worker,
    sky,
    stars,
    post,
    camera,
    intro,
    traffic,
    rig,
    water,
    cloudLighting,
    cloudInteraction,
  ]);

  return {
    worker,
    workerRef,
    badgeRef,
    writeFps,
    writeCamera,
    markReady,
    ready,
    intro,
    cameraFrameRef,
    subscribeCamera,
  };
};
